import React, { useState, useRef } from "react";
import { API, graphqlOperation } from 'aws-amplify';
import { createProduct } from '../graphql/mutations';
import { Button, Notification, Progress, Message } from "element-react";
import { convertDollarsToCents } from '../utils';
import Papa from 'papaparse';
import styled from 'styled-components';

const UploadWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
`;

const DropZone = styled.div`
  width: 100%;
  max-width: 500px;
  padding: 40px;
  border: 2px dashed rgba(1, 191, 113, 0.4);
  border-radius: 16px;
  text-align: center;
  background: rgba(1, 191, 113, 0.04);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.08);
    border-color: #01bf71;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileInfo = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #1f2d3d;
`;

const SummaryBox = styled.div`
  text-align: center;
`;

const ErrorList = styled.ul`
  color: #d32f2f;
  text-align: left;
  background: rgba(239, 83, 80, 0.1);
  padding: 16px 24px;
  border-radius: 8px;
`;

const BulkUpload = ({ marketId }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = () => {
    const csvContent = "data:text/csv;charset=utf-8,Description,Price,Shipped,StockQuantity,Category\nSample Product,9.99,true,50,Spices & Masalas";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "bulk_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        const validationErrors = [];
        
        data.forEach((row, index) => {
          if (!row.Description) validationErrors.push(`Row ${index + 1}: Missing Description`);
          if (!row.Price || isNaN(row.Price)) validationErrors.push(`Row ${index + 1}: Invalid or missing Price`);
        });

        if (validationErrors.length > 0) {
          setErrors(validationErrors);
          setParsedData([]);
        } else {
          setErrors([]);
          setParsedData(data);
        }
      },
      error: (err) => {
        Message({
          type: 'error',
          message: 'Error parsing CSV file'
        });
      }
    });
  };

  const executeUpload = async () => {
    if (parsedData.length === 0) return;
    setIsUploading(true);
    setProgress(0);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < parsedData.length; i++) {
      const row = parsedData[i];
      try {
        const input = {
          MarketProducts: marketId,
          description: row.Description,
          price: convertDollarsToCents(row.Price),
          shipped: String(row.Shipped).toLowerCase() === 'true',
          stockQuantity: row.StockQuantity ? parseInt(row.StockQuantity, 10) : 50,
          category: row.Category ? row.Category.trim() : "Pantry & Groceries"
        };

        await API.graphql(graphqlOperation(createProduct, { input }));
        successCount++;
      } catch (err) {
        console.error('Error uploading product row', i, err);
        failCount++;
      }
      
      setProgress(Math.round(((i + 1) / parsedData.length) * 100));
    }

    setIsUploading(false);
    
    if (failCount === 0) {
      Notification({
        title: "Success",
        message: `Successfully uploaded ${successCount} products!`,
        type: "success"
      });
      setFile(null);
      setParsedData([]);
      setProgress(0);
    } else {
      Notification({
        title: "Warning",
        message: `Uploaded ${successCount} products, but ${failCount} failed. Check console for details.`,
        type: "warning"
      });
    }
  };

  return (
    <UploadWrap>
      <h3>Bulk Upload Products</h3>
      <Button type="text" icon="document" onClick={handleDownloadTemplate}>
        Download CSV Template
      </Button>

      <DropZone onClick={() => fileInputRef.current && fileInputRef.current.click()}>
        <HiddenInput 
          type="file" 
          accept=".csv" 
          ref={fileInputRef} 
          onChange={handleFileChange} 
        />
        {file ? (
          <FileInfo>Selected: {file.name}</FileInfo>
        ) : (
          <p>Drag and drop a CSV file here, or click to select</p>
        )}
      </DropZone>

      {errors.length > 0 && (
        <ErrorList>
          {errors.map((err, i) => (
            <li key={i}>{err}</li>
          ))}
        </ErrorList>
      )}

      {parsedData.length > 0 && !isUploading && (
        <SummaryBox>
          <p>Ready to upload <strong>{parsedData.length}</strong> products.</p>
          <Button type="primary" onClick={executeUpload}>
            Start Upload
          </Button>
        </SummaryBox>
      )}

      {isUploading && (
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <Progress type="line" percentage={progress} />
          <p style={{ textAlign: 'center' }}>Uploading...</p>
        </div>
      )}
    </UploadWrap>
  );
};

export default BulkUpload;
